import { Component, ViewChild } from '@angular/core';
import { InsuranceTableComponent } from '../../../componentes/insurance-table/insurance-table.component';
import { ExportarComponent } from '../../../componentes/exportar/exportar.component';
import { CamposComponent } from '../../../componentes/campos/campos.component';
import { TableConfig } from '../../../interfaces/table-config.interface';
import { CancelacionesFilter } from '../../../interfaces/filters.interfaces';
import {
  PolicyCancellationRequest,
  PolicyCancellationResponse,
} from '../../../interfaces/micartera.interfaces';
import { ApiResponse } from '../../../interfaces/common.interfaces';
import { MicarteraService } from '../../../services/micartera.service';
import { AuthService } from '../../../services/auth.service';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';

/**
 * Componente para gestionar las cancelaciones de pólizas
 * Permite visualizar y exportar información sobre las pólizas canceladas
 */
@Component({
  selector: 'app-cancelaciones',
  standalone: true,
  imports: [
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
    CommonModule,
  ],
  templateUrl: './cancelaciones.component.html',
  styleUrls: ['./cancelaciones.component.css'],
})
export class CancelacionesComponent {
  insuranceData: PolicyCancellationResponse[] = [];
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  errorStatus?: number;
  fecha1?: Date;
  fecha2?: Date;
  title: string = '';
  // Configuración de la tabla
  tableConfig: TableConfig = {
    columns: [
      { key: 'policyNumber', label: 'Póliza', sortable: true, type: 'poliza' },
      { key: 'clientCode', label: 'Código', sortable: true },
      { key: 'clientName', label: 'Asegurado', sortable: true },
      {
        key: 'policyEffectiveDate',
        label: 'Inicio vigencia',
        sortable: true,
        type: 'date',
      },
      {
        key: 'expirationDate',
        label: 'Fin vigencia',
        sortable: true,
        type: 'date',
      },
      {
        key: 'premium',
        label: 'Prima anual',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'tax',
        label: 'Impuestos',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'netPremium',
        label: 'Prima neta',
        sortable: true,
        type: 'currency',
      },
      { key: 'cancellationReason', label: 'Motivo', sortable: true },
      {
        key: 'cancellationDate',
        label: 'Fecha cancelacion',
        sortable: true,
        type: 'date',
      },
    ],
    showTotals: true,
    totalFields: ['premium'],
  };
  tableConfigExport: TableConfig = {
    columns: [
      { key: 'policyNumber', label: 'Póliza', sortable: true, type: 'poliza' },
      { key: 'clientCode', label: 'Código', sortable: true },
      { key: 'clientName', label: 'Asegurado', sortable: true },
      {
        key: 'policyEffectiveDate',
        label: 'Inicio vigencia',
        sortable: true,
        type: 'date',
      },
      {
        key: 'expirationDate',
        label: 'Fin vigencia',
        sortable: true,
        type: 'date',
      },
      {
        key: 'slobDescrition',
        label: 'Descripción Ramo',
        sortable: true,
        type: 'text',
      },
      {
        key: 'currencyType',
        label: 'Moneda',
        sortable: true,
        type: 'text',
      },
      {
        key: 'insuredValue',
        label: 'Valor asegurado',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'premium',
        label: 'Prima anual',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'tax',
        label: 'Impuestos',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'netPremium',
        label: 'Prima neta',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'documentNumber',
        label: 'Número documento',
        sortable: true,
        type: 'number',
      },
      { key: 'cancellationReason', label: 'Motivo', sortable: true },
      {
        key: 'cancellationDate',
        label: 'Fecha cancelacion',
        sortable: true,
        type: 'date',
      },
    ],
    showTotals: true,
    totalFields: ['premium'],
  };

  opcionesSelectFilter = [
    { id: 'codigo', nombre: 'Código' },
    { id: 'cedula', nombre: 'Cédula' },
    { id: 'rnc', nombre: 'RNC' },
    { id: 'nombre', nombre: 'Nombre' },
  ];

  // Referencia al componente de la tabla
  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  /**
   * Constructor del componente
   * @param micarteraService Servicio para obtener datos de las cancelaciones
   * @param authService Servicio de autenticación
   */
  constructor(
    private micarteraService: MicarteraService,
    private authService: AuthService,
    private gAnalytics: GoogleAnalyticsService
  ) {
    this.title = 'Mis Cancelaciones';
  }

  /**
   * Maneja los cambios en los filtros de búsqueda
   * Realiza una nueva consulta al servidor con los filtros actualizados
   * @param filters Objeto con los filtros seleccionados por el usuario
   */
  onFilterChange(filters: CancelacionesFilter): void {
    if (Object.keys(filters).length === 0) {
      this.insuranceData = [];
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }

    this.fecha1 = filters.dateFrom;
    this.fecha2 = filters.dateTo;

    const request: PolicyCancellationRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      dateFrom: filters.dateFrom || new Date(),
      dateTo: filters.dateTo || new Date(),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
    };
    // Agregar campos opcionales solo si tienen valor
    if (filters.clientCode) request.clientCode = filters.clientCode;

    this.micarteraService.getPolicyCancellations(request).subscribe({
      next: (
        response: HttpResponse<ApiResponse<PolicyCancellationResponse[]>>
      ) => {
        if (response.status === 200) {
          this.insuranceData = response.body?.data || [];
          this.errorStatus = undefined;
        } else if (response.status === 204) {
          this.insuranceData = [];
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al obtener cancelaciones:', error);
        this.insuranceData = [];
        this.mensajeNoData =
          'No se han podido obtener los datos de las cancelaciones';
      },
    });
  }

  /**
   * Maneja la exportación de datos en diferentes formatos
   * @param formato Formato de exportación deseado (PDF, Excel, etc)
   */
  onExportar(formato: string): void {
    if (this.tablaComponent) {
      const headerInfo = {
        titulo: 'Cancelaciones',
        lob: this.getLobName(),
        slob: this.getSlobName(),
        fecha1: this.fecha1!,
        fecha2: this.fecha2,
        tableConfig: this.tableConfigExport,
      };



      //Evento para trackear las exportaciones de archivos en Google Analytics
      this.gAnalytics.trackEvent('cancelaciones_exportar',{
        page:'/cancelaciones',
        format:formato
      })

      this.tablaComponent.exportarDatos(formato, headerInfo);
    }
  }

  // Extrayendo los nombres de los ramos y subramos
  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  /**
   * Obtiene el nombre del ramo (LOB) seleccionado
   * @returns Nombre del ramo o cadena vacía si no hay selección
   */
  private getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  /**
   * Obtiene el nombre del subramo (SLOB) seleccionado
   * @returns Nombre del subramo o cadena vacía si no hay selección
   */
  private getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );
    return selectedSLob?.sLobName || '';
  }
}
